import "../style/manual_testing.css";
import { NavLink } from "react-router-dom";
import ArticleSidebar from "../components/ArticleSidebar";
// Assets
import what_does_manual_tester from "../assets/what-does-manual-tester.png";
import sdlc from "../assets/sdlc.png";
import leveOfTesting from "../assets/levels_of_test.webp"; // Fixed typo
import stlc from "../assets/stlc.png";

export default function ManualTestingPage() {
  return (
    <div className="manual-testing-container">
      {/* Sidebar Navigation */}
      <ArticleSidebar as="aside" className="side-header-wrapper article-sidebar" />

      {/* Main Content Area */}
      <main id="main" className="clearfix">
        <div className="fusion-row">
          <section id="content" style={{ width: "100%" }}>
            <article className="post-article">
              <h1 className="entry-title">Manual Testing Cheat Sheet</h1>
              
              <img src={what_does_manual_tester} alt="What a manual tester does diagram" style={{ width: "100%", borderRadius: "8px" }} />

              <div className="post-content">
                <blockquote className="quote-text">
                  <i>“A great tester is not the one who finds the most bugs, but the one who ensures the right bugs get fixed.”</i>
                  <br />— <b>Manish Kumar</b>
                </blockquote>

                <p>
                  Testing isn’t simply finding bugs; it is knowing how things
                  work, trying to think as a user, and making sure that
                  everything runs smoothly. It’s about digging deep, challenging
                  assumptions, and finding issues before they become actual
                  problems. A great tester is basically not executing the steps:
                  they’re curious, observant, and one step ahead. Whether
                  verifying edge cases, making sure a smooth user experience, or
                  simply checking that code behaves as expected. A whole
                  industry is focused on keeping software reliable, intuitive,
                  and, most importantly, trustworthy.
                </p>
                <p>
                  Whether you’re just getting started with testing or you’re a
                  seasoned QA guru, this cheat sheet will help guide you through
                  all aspects of manual testing. So let’s get started.
                </p>

                {/* What is Manual Testing */}
                <section id="manual-testing-definition" className="content-block">
                  <h1>What is Manual Testing?</h1>
                  <p>
                    Manual testing is a process where test cases are executed
                    manually without using any automation tools. It requires
                    domain knowledge, analytical capabilities and test execution
                    techniques. In manual testing, a human tester plays the role
                    of an end user and interacts with the software to check if
                    it functions as expected.
                  </p>

                  <h3>Key Aspects of Manual Testing</h3>
                  <ul className="styled-list">
                    <li>
                      <b>No Automation Involved:</b> Testers execute test cases
                      without using automation tools. Read:{" "}
                      <NavLink className="manual-teting" to="/manual-vs-automation-testing">
                        <b>Manual vs Automation Testing</b>
                      </NavLink>
                    </li>
                    <li>
                      <b>Human Judgment:</b> Testers use intuition, domain
                      knowledge, and real-world scenarios.
                    </li>
                    <li>
                      <b>Defect Identification:</b> The primary goal is to find
                      bugs and missing features.
                    </li>
                    <li>
                      <b>User Perspective Testing:</b> Makes sure that the
                      software meets customer expectations.
                    </li>
                  </ul>
                </section>
                <section id="tester-responsibilities" className="content-block">
                  <h1>What Does a Tester Do?</h1>
                  <p>
                    A <b>manual tester</b> is responsible for maintaining the
                    software quality through various activities.
                  </p>
                  <img
                    src={what_does_manual_tester}
                    alt="Manual Testing"
                    style={{ width: "100%" }}
                  />
                </section>

                {/* SDLC Section */}
                <section id="sdlc-stlc" className="content-block">
                  <h1>Software Development Life Cycle</h1>
                  <p>
                    The{" "}
                    <NavLink className="manual-teting" to="/what-is-sdlc" >
                      {" "}
                      <b>Software Development Life Cycle (SDLC)</b>
                    </NavLink>{" "}
                    defines the process of planning, developing, testing, and
                    deploying software.
                  </p>
                  <img
                    src={sdlc}
                    alt="Manual Testing"
                    style={{ width: "50%" }}
                  />

                  <h3>Phases of SDLC</h3>
                  <ul className="styled-list">
                    <li>
                      <b>Requirement Gathering</b>: Understanding business and
                      user needs.
                    </li>
                    <li>
                      <b>Planning</b>: Defining scope, resources, and risk
                      management.
                    </li>
                    <li>
                      <b>Design</b>: Creating system architecture and UI/UX
                      design.
                    </li>
                    <li>
                      <b>Development</b>: Writing code for the application.
                    </li>
                    <li>
                      <b>Testing</b>: Validating the application manually and
                      through automation.
                    </li>
                    <li>
                      <b>Maintenance</b>: Updating and improving the software
                      post-release.
                    </li>
                  </ul>
                  <p>
                    Testing is crucial in SDLC to maintain quality, reliability,
                    and performance.
                  </p>
                  <h3>Comparing SDLC Methodologies</h3>
                  <div className="table-responsive">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>SDLC Model</th>
                          <th>Description</th>
                          <th>Pros</th>
                          <th>Cons</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <th>Waterfall Model</th>
                          <td>
                            A structured sequential manner in which each phase
                            must be finished before moving to the next.
                          </td>
                          <td>
                            <ul>
                              <li>Easy to manage and document.</li>
                              <li>Well-organized and clear requirements.</li>
                              <li>Best for small, well-defined projects.</li>
                            </ul>
                          </td>
                          <td>
                            <ul>
                              <li>
                                Not flexible; changes are difficult to
                                accommodate.
                              </li>
                              <li>
                                Defect detection becomes increasingly expensive
                                as testing is performed late in the development
                                lifecycle.
                              </li>
                              <li>
                                Not Ideal For Complex Or Evolving Projects
                              </li>
                            </ul>
                          </td>
                        </tr>
                        <tr>
                          <th>Iterative Model</th>
                          <td>
                            Software is developed and improved through repeated
                            cycles
                          </td>
                          <td>
                            <ul>
                              <li>
                                Detects risks early and allows continuous
                                improvement.
                              </li>
                              <li>More flexible than Waterfall.</li>
                              <li>Suitable for complex projects.</li>
                            </ul>
                          </td>
                          <td>
                            <ul>
                              <li>Requires more resources and time.</li>
                              <li>Needs frequent customer involvement.</li>
                              <li>
                                Managing multiple iterations can be complex.
                              </li>
                            </ul>
                          </td>
                        </tr>
                        <tr>
                          <th>Spiral Model</th>
                          <td>
                            Merges iterative and risk-based methods over many
                            development stages (planning, risk evaluation,
                            design, and assessment).
                          </td>
                          <td>
                            <ul>
                              <li>Ideal for high-risk projects.</li>
                              <li>Focuses on risk analysis and user input.</li>
                              <li>Allows iterative development.</li>
                            </ul>
                          </td>
                          <td>
                            <ul>
                              <li>
                                Overpriced is the cause of the very expensive
                                risk analysis.
                              </li>
                              <li>Requires skilled expertise.</li>
                              <li>Not suitable for small projects.</li>
                            </ul>
                          </td>
                        </tr>
                        <tr>
                          <td>Agile Model</td>
                          <td>
                            A flexible and iterative approach where development
                            is broken into small cycles (sprints).
                          </td>
                          <td>
                            <ul>
                              <li>
                                Highly adaptable to changing requirements.
                              </li>
                              <li>
                                Encourages collaboration and continuous
                                feedback.
                              </li>
                              <li>Faster delivery of functional software.</li>
                            </ul>
                          </td>
                          <td>
                            <ul>
                              <li>Requires frequent customer involvement.</li>
                              <li>Needs skilled and self-organizing teams.</li>
                              <li>
                                It can be challenging to manage in large teams.
                              </li>
                            </ul>
                          </td>
                        </tr>
                        <tr>
                          <td>RAD (Rapid Application Development)</td>
                          <td>
                            A quick (compared to waterfall) approach to
                            development that focuses on prototyping and user
                            feedback.
                          </td>
                          <td>
                            <ul>
                              <li>Quick delivery of functional software.</li>
                              <li>
                                Early feedback leads to high customer
                                satisfaction.
                              </li>
                              <li>Encourages collaboration.</li>
                            </ul>
                          </td>
                          <td>
                            <ul>
                              <li>Needs constant participation of the user.</li>
                              <li>Not a good fit for big projects.</li>
                              <li>
                                Interdependence on high-skilled developers.
                              </li>
                            </ul>
                          </td>
                        </tr>
                        <tr>
                          <th>DevOps</th>
                          <td>
                            Integrates development and operations teams for
                            continuous development, testing, and deployment.
                          </td>
                          <td>
                            <ul>
                              <li>
                                Faster releases with automated testing and
                                deployment.
                              </li>
                              <li>Improves collaboration between teams.</li>
                              <li>
                                Enhances software reliability and quality.
                              </li>
                            </ul>
                          </td>
                          <td>
                            <ul>
                              <li>
                                Requires cultural shift and tool integration.
                              </li>
                              <li>Needs skilled professionals.</li>
                              <li>
                                It can be complex to implement in legacy
                                systems.
                              </li>
                            </ul>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </section>
                {/* Testing Methods */}
                <section className="content-block">
                  <h1
                    data-fontsize="48"
                    data-lineheight="57.6px"
                    className="fusion-responsive-typography-calculated"
                    style={{ fontSize: "48", lineheight: "1.2" }}
                  >
                    Testing Methods
                  </h1>
                  <p>
                    There are three main methods used in{" "}
                    <NavLink className="manual-teting"  to="/software-testing">
                      software testing
                    </NavLink>
                    :
                  </p>

                  <div className="table-responsive">
                    <table className="data table">
                      <tbody>
                        <tr>
                          <th>Black Box Testing</th>
                          <td>
                            <ul>
                              <li>
                                Focuses on software functionality without
                                looking at code.
                              </li>
                              <li>
                                Testers validate inputs and outputs without
                                knowing the internal implementation.
                              </li>
                              <li>
                                <b>Examples</b>: Functional Testing, UI Testing,
                                User Acceptance Testing (UAT).
                              </li>
                            </ul>
                          </td>
                        </tr>
                        <tr>
                          <th>White Box Testing</th>
                          <td>
                            <ul>
                              <li>
                                Involves testing the internal structure of an
                                application.
                              </li>
                              <li>
                                Requires programming knowledge to analyze logic
                                and code execution. Read:{" "}
                                <NavLink className="manual-teting" to="/what-is-white-box-and-black-box-testing">
                                  What is White Box and Black Box Testing?
                                </NavLink>
                              </li>
                              <li>
                                <b>Examples</b>: Unit Testing, Code Coverage
                                Testing, etc.
                              </li>
                            </ul>
                          </td>
                        </tr>
                        <tr>
                          <th>Gray Box Testing</th>
                          <td>
                            <ul>
                              <li>
                                A combination of Black Box and White Box
                                Testing.
                              </li>
                              <li>
                                Testers have partial knowledge of internal
                                workings.
                              </li>
                              <li>
                                Used to validate security and data flow across
                                modules.
                              </li>
                            </ul>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </section>

                {/* Essential Manual Testing Concepts */}

                <section id="test-cases-defects">
                  <h1
                    data-fontsize="48"
                    data-lineheight="57.6px"
                    className="fusion-responsive-typography-calculated"
                    style={{ fontSize: "48", lineheight: "1.2" }}
                  >
                    Essential Manual Testing Concepts
                  </h1>

                  <div className="table-responsive">
                    <table className="table table-bordered">
                      <tbody>
                        <tr>
                          <th>Test Case</th>
                          <td>
                            A structured set of inputs, execution conditions,
                            and expected results is designed to verify a
                            specific feature or functionality of the
                            application. Read:{" "}
                            <NavLink className="manual-teting" to="/test-cases-vs-test-suites-understanding-vs-difference">
                              Test Cases vs Test Suites: Understanding the
                              Differences
                            </NavLink>
                          </td>
                        </tr>
                        <tr>
                          <th>Test Scenario</th>
                          <td>
                            A high-level description of a test objective that
                            covers multiple test cases to validate a particular
                            workflow or user interaction.
                          </td>
                        </tr>
                        <tr>
                          <th>Defect/Bug</th>
                          <td>
                            An error, flaw, or deviation in the software that
                            causes it to behave unexpectedly or incorrectly
                            compared to the specified requirements.
                          </td>
                        </tr>
                        <tr>
                          <th>Test Plan</th>
                          <td>
                            A comprehensive document outlining the testing
                            strategy, scope, objectives, resources, schedule,
                            and responsibilities for a testing cycle. Read:{" "}
                            <NavLink className="manual-teting" to="/test-plan-template">
                              Test Plan Template
                            </NavLink>
                          </td>
                        </tr>
                        <tr>
                          <th>Test Environment</th>
                          <td>
                            The configured hardware, software, network, and
                            other necessary tools are required to execute test
                            cases in a controlled setting. Read:{" "}
                            <NavLink className="manual-teting" to="/test-environment-page">
                              What is a Test Environment?
                            </NavLink>
                          </td>
                        </tr>
                        <tr>
                          <th>Severity vs. Priority</th>
                          <th>
                            <div>
                              <b>Severity</b> represents the impact of a defect
                              on system functionality.
                            </div>
                            <div>
                              <b>Priority</b> determines the urgency with which
                              it should be fixed.
                            </div>
                          </th>
                        </tr>
                        <tr>
                          <th>DRE (Defect Removal Efficiency)</th>
                          <td>
                            <div>
                              Measures the number of defects found internally.
                            </div>
                            <div>
                              DRE = (Total Defects Found – Total Defects Found
                              in Production) / Total Defects Found
                            </div>
                          </td>
                        </tr>
                        <tr>
                          <th>Defect Tags</th>
                          <td>
                            Descriptive labels are used to categorize and
                            organize defects for easier analysis, retrieval, and
                            filtering.
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </section>
                {/* QA vs QC Section */}
                <section className="content-block">
                  <h1>
                    Difference between Quality Assurance (QA) and Quality
                    Control (QC)
                  </h1>

                  <p>
                    The following table summarizes the key differences between
                    QA and QC.
                  </p>
                  <div className="table-responsive">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Parameters</th>
                          <th>Quality Assurance (QA)</th>
                          <th>Quality Control (QC)</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <th>Objective</th>
                          <td>
                            QA is a process of providing assurance that the
                            quality requested will be achieved.
                          </td>
                          <td>
                            QC is a process of fulfilling the quality requested.
                          </td>
                        </tr>
                        <tr>
                          <th>Technique</th>
                          <td>QA is the technique of managing quality.</td>
                          <td>QC is the technique to verify quality.</td>
                        </tr>
                        <tr>
                          <th>Involved in which phase?</th>
                          <td>QA is involved during the development phase.</td>
                          <td>
                            QC is not included during the development phase.
                          </td>
                        </tr>
                        <tr>
                          <th>Program execution is included?</th>
                          <td>
                            There is no execution of the program during QA.
                          </td>
                          <td>
                            QC always includes the execution of the program.
                          </td>
                        </tr>
                        <tr>
                          <th>Type of tool</th>
                          <td>QA is a managerial tool.</td>
                          <td>QC is a corrective tool.</td>
                        </tr>
                        <tr>
                          <th>Process/ Product-oriented</th>
                          <td>QA is process-oriented.</td>
                          <td>QC is product-oriented.</td>
                        </tr>
                        <tr>
                          <th>Aim</th>
                          <td>QA aims at preventing defects.</td>
                          <td>QC identifies and improves the defects.</td>
                        </tr>
                        <tr>
                          <th>Order of execution</th>
                          <td>
                            Quality Assurance is performed before Quality
                            Control.
                          </td>
                          <td>
                            Quality Control is performed after the Quality
                            Assurance activity is done.
                          </td>
                        </tr>
                        <tr>
                          <th>Technique type</th>
                          <td>QA is a preventive technique.</td>
                          <td>QC is a corrective technique.</td>
                        </tr>
                        <tr>
                          <th>SDLC/ STLC?</th>
                          <td>
                            QA is responsible for the entire software
                            development life cycle.
                          </td>
                          <td>
                            QC is responsible for the software testing life
                            cycle.
                          </td>
                        </tr>
                        <tr>
                          <th>Team</th>
                          <td>The entire project team is involved.</td>
                          <td>The project testing team is involved.</td>
                        </tr>
                        <tr>
                          <th>
                            Time consumption <b></b>
                          </th>
                          <td>QA is less time-consuming.</td>
                          <td>QC is more time-consuming.</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </section>

                {/* Levels of Testing */}
                <section className="content-block">
                  <h1>Levels of Testing</h1>

                  <p>
                    Software testing is conducted at different levels to ensure
                    quality.
                  </p>

                  <img
                    src={leveOfTesting}
                    alt="Level of Testing"
                    style={{ width: "50%" }}
                  />
                  <div className="table-responsive">
                    <table className="data-table">
                      <tbody>
                        <tr>
                          <th>Unit Testing</th>
                          <td>
                            <ul>
                              <li>
                                Developers do this testing at the code level
                                before integrating components.
                              </li>
                              <li>
                                Tests the individual functions, methods, or
                                modules in isolation.
                              </li>
                              <li>
                                It assists in finding early-stage bugs and
                                enhances code quality.
                              </li>
                              <li>
                                Usually automated with frameworks such as JUnit
                                (Java), NUnit (. NET), or PyTest (Python).
                              </li>
                            </ul>
                          </td>
                        </tr>
                        <tr>
                          <th>Integration Testing</th>
                          <td>
                            <ul>
                              <li>
                                Tests various modules, services, or APIs to
                                ensure they are interacting as expected
                              </li>
                              <li>
                                Detects communication failures between
                                components, like data flow errors.
                              </li>
                              <li>
                                Includes different techniques like Top-Down,
                                Bottom-Up, and Big Bang Testing.
                              </li>
                              <li>
                                Some common tools are testRigor, Postman, etc.
                              </li>
                            </ul>
                          </td>
                        </tr>
                        <tr>
                          <th>System Testing</th>
                          <td>
                            <ul>
                              <li>
                                Tests and evaluates the complete software system
                                as a unique system after integration.
                              </li>
                              <li>
                                Checks that all end-to-end functionality is as
                                per business and technical requirements.
                              </li>
                              <li>
                                Encompasses functional, performance, security,
                                and usability testing.
                              </li>
                              <li>
                                Tests are executed in an environment that is
                                similar to production.
                              </li>
                            </ul>
                          </td>
                        </tr>
                        <tr>
                          <th>User Acceptance Testing (UAT)</th>
                          <td>
                            <ul>
                              <li>
                                End-user or stakeholder-driven tests to validate
                                real-world business needs.
                              </li>
                              <li>
                                Emphasizes the need to ensure the software works
                                according to customer satisfaction before
                                deployment.
                              </li>
                              <li>
                                End-user or stakeholder-driven tests to validate
                                real-world business needs.
                              </li>
                              <li>
                                It is the final phase before releasing it into
                                production, identifying any last-minute defects.
                              </li>
                            </ul>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </section>

                {/* Types of Testing */}
                <section className="content-block">
                  <h1>Types of Testing</h1>

                  <p>
                    Manual testing consists of various testing types, each
                    designed to validate different aspects of software
                    functionality, performance, and security.
                  </p>

                  <div className="table-responsive">
                    <table className="data-table">
                      <tbody>
                        <tr>
                          <th>Functional Testing</th>
                          <td>
                            Tests that the application behaves as expected by
                            validating its features against specified
                            requirements. Read:{" "}
                            <NavLink className="manual-teting" to="/functional-testing-guide">
                              Functional Testing Types
                            </NavLink>
                          </td>
                        </tr>
                        <tr>
                          <th>Non-Functional Testing</th>
                          <td>
                            Evaluates aspects like performance, security, and
                            usability to make sure the software meets quality
                            standards beyond just functionality.
                          </td>
                        </tr>
                        <tr>
                          <th><NavLink to="/what-is-smoke-testing">Smoke Testing</NavLink></th>
                          <td>
                            A quick, high-level test to verify that the critical
                            functionalities of the application work before
                            proceeding with more detailed testing.
                          </td>
                        </tr>
                        <tr>
                          <th><NavLink to="/what-is-sanity">Sanity Testing</NavLink></th>
                          <td>
                            A focused check on specific functionalities or
                            recent fixes to confirm they work correctly without
                            conducting a full regression test.
                          </td>
                        </tr>
                        <tr>
                          <th>Regression Testing</th>
                          <td>
                            Make sure that new changes, bug fixes, or
                            enhancements do not negatively impact previously
                            working functionality. Read:{" "}
                            <NavLink className="manual-teting" to="/regression-testing">
                              What is Regression Testing?
                            </NavLink>
                          </td>
                        </tr>
                        <tr>
                          <th>Exploratory Testing</th>
                          <td>
                            Performed without predefined test cases, relying on
                            tester intuition and experience to uncover defects.
                          </td>
                        </tr>
                        <tr>
                          <th>Ad-hoc Testing</th>
                          <td>
                            An unstructured testing approach where testers
                            randomly explore the application to find unexpected
                            issues.
                          </td>
                        </tr>
                        <tr>
                          <th>Performance Testing</th>
                          <td>
                            Assess the application’s speed, responsiveness, and
                            stability under different conditions to identify
                            performance bottlenecks. Read:{" "}
                            <NavLink className="manual-teting" to="/what-is-performance-testing">
                              Performance Testing Types
                            </NavLink>
                          </td>
                        </tr>
                        <tr>
                          <th>Security Testing</th>
                          <td>
                            To check robust security measures, examine the
                            system for vulnerabilities, unauthorized access, and
                            data breaches.
                          </td>
                        </tr>
                        <tr>
                          <th>Accessibility Testing</th>
                          <td>
                            Test that the application is usable for individuals
                            with special needs, following standards like WCAG
                            for inclusivity.
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </section>

                {/* Test Case Design Techniques */}

                <section className="content-block">
                  <h1>Test Case Design Techniques</h1>
                  <div className="table-responsive">
                    <table className="data-table">
                      <tbody>
                        <tr>
                          <th>Equivalence Partitioning</th>
                          <td>
                            A technique that divides input data into valid and
                            invalid partitions to minimize the number of test
                            cases while having maximum test coverage.
                          </td>
                        </tr>
                        <tr>
                          <th>Boundary Value Analysis</th>
                          <td>
                            It focuses on testing input values at the extreme
                            edges (minimum, maximum, and just outside limits) to
                            detect boundary-related defects.
                          </td>
                        </tr>
                        <tr>
                          <th>Decision Table Testing</th>
                          <td>
                            Uses a tabular format to represent different input
                            conditions and their corresponding expected outputs,
                            making sure that all possible scenarios are tested.
                          </td>
                        </tr>
                        <tr>
                          <th>State Transition Testing</th>
                          <td>
                            Evaluates the system’s behavior by testing valid and
                            invalid state changes to verify that transitions
                            between states function correctly.
                          </td>
                        </tr>
                        <tr>
                          <th>Use Case Testing</th>
                          <td>
                            Validates software functionality based on real-world
                            user interactions and workflows to make sure that
                            theapplication meets business requirements.
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </section>

                {/* Software Testing Life Cycle */}
                <section className="content-block">
                  <h1 style={{ fontSize: "90" }}>
                    Software Testing Life Cycle
                  </h1>
                  <img
                    src={stlc}
                    alt="Level of Testing"
                    style={{ width: "70%" }}
                  />
                  <div className="table-responsive">
                    <table className="data-table">
                      <tbody>
                        <tr>
                          <th>Requirement Analysis</th>
                          <td>
                            Involves understanding the project requirements and
                            defining the test scope to ensure all
                            functionalities are covered.
                          </td>
                        </tr>
                        <tr>
                          <th>Test Planning</th>
                          <td>
                            Focuses on creating the test strategy, defining test
                            objectives, estimating resources, and scheduling
                            test activities.
                          </td>
                        </tr>
                        <tr>
                          <th>Test Case Development</th>
                          <td>
                            Involves writing detailed test cases, preparing test
                            data, and defining expected outcomes based on
                            requirements.
                          </td>
                        </tr>
                        <tr>
                          <th>Test Environment Setup</th>
                          <td>
                            Prepares the necessary hardware, software, network
                            configurations, and access permissions to execute
                            tests.
                          </td>
                        </tr>
                        <tr>
                          <th>Test Execution</th>
                          <td>
                            Running test cases, comparing actual vs. expected
                            results, and logging defects for resolution.
                          </td>
                        </tr>
                        <tr>
                          <th>Test Closure</th>
                          <td>
                            Finalizing documentation, analyzing test metrics,
                            preparing closure reports, and discussing lessons
                            learned for future improvements.
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </section>

                <section className="content-block">
                  <h1>Error vs. Bug vs. Defect vs. Fault vs. Failure</h1>
                  <p>
                    Software testing is mainly performed to identify errors,
                    deficiencies, or missing requirements in the application. It
                    is important because if there are any errors or bugs in the
                    application, they can be identified and fixed before the
                    application is released.
                  </p>

                  <p>
                    In software testing and development, the terms “error”,
                    “defect”, “bug”, “fault”, and “failure” are related but have
                    distinct meanings. These terms are explained below:
                  </p>
                  <h2>Error</h2>
                  <p>
                    An error is a mistake made by a developer, programmer or any
                    other human involved in the development of an application.
                    Typos, logical errors, or incorrect assumptions are all
                    errors.
                  </p>
                  <ul>
                    <li>
                      Errors occur because of wrong logic, problematic syntax or
                      loops, or typing mistakes.
                    </li>
                    <li>
                      Error is identified by comparing actual results with the
                      expected results.
                    </li>
                  </ul>

                  <h2>Bug</h2>
                  <p>
                    A bug is an informal term for defect and used to describe a
                    situation where the software product or application does not
                    work as per the requirements. When the bug is present, the
                    application breaks.
                  </p>
                  <ul>
                    <li>
                      A bug once identified can be reproduced with the help of
                      standard bug-reporting templates.
                    </li>
                    <li>
                      The bugs that may cause user dissatisfaction are
                      considered major bugs and have to be fixed on priority.
                    </li>
                    <li>Application crash is the most common type of bug.</li>
                  </ul>

                  <h2>Defect</h2>
                  <p>
                    A defect is a flaw or a problem in the software that causes
                    it to deviate from its expected behavior.
                  </p>
                  <ul>
                    <li>A defect can affect the whole program.</li>
                    <li>
                      A defect represents the inability of the application to
                      meet the criteria. It prevents the application from
                      performing its desired work.
                    </li>
                    <li>
                      Defects occur when a developer makes mistakes during the
                      development.
                    </li>
                  </ul>

                  <h2>Fault</h2>
                  <p>
                    Fault is the term used to describe an error that occurs due
                    to certain factors such as lack of resources or specific
                    steps not followed properly.
                  </p>
                  <ul>
                    <li>
                      A fault is an undesirable situation that occurs mainly due
                      to invalid documented steps or a lack of data definitions.
                    </li>
                    <li>
                      It is an unintentional behavior by an application program
                      and causes a warning to the program.
                    </li>
                    <li>
                      If left unchecked, a fault, however minor, may lead to
                      failure in the application.
                    </li>
                  </ul>

                  <h2>Failure</h2>
                  <p>
                    Failure is the inability of a system or application to
                    perform its intended function within specified criteria and
                    limits. When defects go undetected, failure occurs,
                    resulting in the application not working properly.
                  </p>
                  <ul>
                    <li>
                      Failure can also be an accumulation of several defects
                      over time.
                    </li>
                    <li>
                      The system or application may become unresponsive as a
                      result of failure.
                    </li>
                    <li>Failures are usually detected by end users.</li>
                  </ul>
                </section>

                <section>
                  <h1>Bug Life Cycle </h1>
                  <p>
                    In software testing, the bug life cycle is the process a bug
                    follows from its initial discovery to its resolution and
                    closure. Bug life cycle is a systematic way of managing
                    defects or bugs, ensuring they are detected, reported,
                    documented, tracked, and fixed.
                  </p>
                  <p>
                    Here are the steps that are followed in a typical bug life
                    cycle.
                  </p>
                  <ol>
                    <li>
                      <b>Detect Defect</b>: The bug is discovered during testing
                      and reported by the tester, usually including details like
                      the steps to reproduce it and its severity.
                    </li>
                    <li>
                      <b>Report Defect</b>: The QA team evaluates the severity
                      and impact of the bug to determine its priority. The bug
                      is assigned to a developer who will be responsible for
                      fixing it.
                    </li>
                    <li>
                      <b>Resolve Defect</b>: The developer addresses the bug and
                      creates a fix. The tester verifies the fix and confirms
                      that the bug is resolved.
                    </li>
                    <li>
                      <b>Re-test Defect</b>: If the bug is found to still exist
                      after the initial fix, it is re-assigned to the developer
                      for further investigation and resolution.
                    </li>
                    <li>
                      <b>Close Defect</b>: Once verified as fixed, the bug is
                      closed.
                    </li>
                  </ol>
                </section>

                {/* Bug Life Cycle */}
                <section className="content-block">
                  <h1>Bug Status Cycle</h1>
                  <p>
                    A bug status cycle consists of different statuses a bug
                    assumes from its initial reporting to its resolution and
                    closure. Bug status cycle helps in tracking the progress of
                    fixing a bug and ensuring effective management of defects.
                  </p>
                  <p>
                    The following are different stages in a bug status cycle:
                  </p>
                  <hr />
                  <div className="status-badge-container">
                    <span className="status-badge new">New</span>
                    <span className="status-badge assigned">Assigned</span>
                    <span className="status-badge open">Open</span>
                    <span className="status-badge fixed">Fixed</span>
                    <span className="status-badge pending-retest">
                      Pending retest
                    </span>
                    <span className="status-badge retest">Retest</span>
                    <span className="status-badge reopened">Reopened</span>
                    <span className="status-badge duplicate">Duplicate</span>
                    <span className="status-badge deferred">Deferred</span>
                    <span className="status-badge verified">Verified</span>
                    <span className="status-badge rejected">Rejected</span>
                    <span className="status-badge not-a-defect">
                      Not a defect
                    </span>
                    <span className="status-badge closed">Closed</span>
                  </div>
                  <hr />
                  <ul>
                    <li>
                      <b>New</b>: The bug is reported for the first time and
                      documented. It may be in a system like Jira.
                    </li>
                    <li>
                      <b>Assigned</b>: The bug is assigned to a developer team
                      for analysis and resolution.
                    </li>
                    <li>
                      <b>Open</b>: The bug is being resolved by the developer.
                    </li>
                    <li>
                      <b>Fixed</b>: The bug fix has been implemented, and the
                      bug is marked resolved.
                    </li>
                    <li>
                      <b>Pending retest</b>: The bug is marked as fixed but is
                      awaiting retesting by the tester.
                    </li>
                    <li>
                      <b>Retest</b>: The fix is verified by the tester to see if
                      the issue is resolved and the fix is working as expected.
                    </li>
                    <li>
                      <b>Verified</b>: If the fix is retested successfully, the
                      bug is marked as verified.
                    </li>
                    <li>
                      <b>Reopened</b>: If it is found during retesting that the
                      fix is not working as expected, the bug is reopened, and
                      the developer team is notified.
                    </li>
                    <li>
                      <b>Closed</b>: Once the bug fix is verified and the code
                      is confirmed to be free of defects, it is marked as
                      closed.
                    </li>
                    <li>
                      <b>Duplicate</b>: If the same bug is reported multiple
                      times, it is marked as a duplicate to avoid duplicate
                      work.
                    </li>
                    <li>
                      <b>Deferred</b>: The bug is acknowledged, but its
                      resolution is postponed due to low priority or other
                      reasons.
                    </li>
                    <li>
                      <b>Rejected</b>: If a reported bug is not a true bug or
                      considered for enhancement, the bug is marked rejected.
                    </li>
                    <li>
                      <b>Not a defect</b>: If a bug is a feature or a minor
                      issue that does not impact the core functionality, the bug
                      is marked Not a Defect.
                    </li>
                  </ul>
                </section>

                {/* Test Metrics Section */}
                <section className="content-block">
                  <h1>Test Metrics</h1>
                  <p>
                    Testing metrics are quantitative measures used to assess the
                    efficiency, effectiveness, and quality of the software
                    testing process. They provide insights into test coverage,
                    defect density, and overall test performance.
                  </p>
                  <div className="metric-card-grid">
                    <div className="metric-item">
                      <h4>Pass Rate</h4>
                      <code>(Passed / Executed) x 100</code>
                    </div>
                    <div className="metric-item">
                      <h4>Defect Density</h4>
                      <code>Total Defects / Size of Module</code>
                    </div>
                    <div className="metric-item">
                      <h4>Leakage</h4>
                      <code>(UAT Defects / Total Defects) x 100</code>
                    </div>
                  </div>

                  <table className="test-matrics">
                    <tbody>
                      <tr>
                        <th>Test Case Execution Percentage</th>
                        <td>
                          <ul>
                            <li>Measures the progress of test execution.</li>
                            <li>
                              (Number of Test Cases Executed / Total Test Cases)
                              x 100
                            </li>
                          </ul>
                        </td>
                      </tr>
                      <tr>
                        <th>Test Case Pass Rate</th>
                        <td>
                          <ul>
                            <li>
                              Indicates the percentage of successful test cases.
                            </li>
                            <li>
                              (Number of Passed Test Cases / Total Executed Test
                              Cases) x 100
                            </li>
                          </ul>
                        </td>
                      </tr>
                      <tr>
                        <th>Test Case Failure Rate</th>
                        <td>
                          <ul>
                            <li>Helps identify defect-prone areas.</li>
                            <li>
                              (Number of Failed Test Cases / Total Executed Test
                              Cases) x 100
                            </li>
                          </ul>
                        </td>
                      </tr>
                      <tr>
                        <th>Test Coverage</th>
                        <td>
                          <ul>
                            <li>
                              Assess how well the requirements are tested.
                            </li>
                            <li>
                              (Number of Requirements Covered by Test Cases /
                              Total Requirements) x 100
                            </li>
                          </ul>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </section>

                {/* Defect Metrics Section */}
                <section className="content-block">
                  <h1>Defect Metrics</h1>

                  <table className="defect-matrics">
                    <tbody>
                      <tr>
                        <th>Defect Density</th>
                        <td>
                          <ul>
                            <li>
                              Determines the number of defects per unit of code
                              (e.g., per
                              <br />
                              1,000 lines of code).
                            </li>
                            <li>
                              Total Number of Defects / Size of the Software
                              Module
                            </li>
                          </ul>
                        </td>
                      </tr>
                      <tr>
                        <th>Defect Leakage</th>
                        <td>
                          <ul>
                            <li>
                              Indicates the percentage of defects missed in
                              earlier test phases
                              <br />
                              but found in production.
                            </li>
                            <li>
                              (Defects Found in UAT / Total Defects Found in
                              Testing) x 100
                            </li>
                          </ul>
                        </td>
                      </tr>
                      <tr>
                        <th>Defect Severity Index (DSI)</th>
                        <td>
                          <ul>
                            <li>
                              Helps prioritize defect resolution based on
                              impact.
                            </li>
                            <li>
                              (Σ (Severity Level x Number of Defects)) / Total
                              Defects
                            </li>
                          </ul>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </section>
                {/* Test Efficiency and Productivity Metrics Section */}
                <section className="content-block">
                  <h1>Test Efficiency and Productivity Metrics</h1>

                  <table className="defect-matrics">
                    <tbody>
                      <tr>
                        <th>Test Efficiency</th>
                        <td>
                          <ul>
                            <li>
                              Measures the effectiveness of the testing process.
                            </li>
                            <li>
                              (Number of Defects Found by Testing / Total
                              Defects in the System) x 100
                            </li>
                          </ul>
                        </td>
                      </tr>
                      <tr>
                        <th>Test Efficiency</th>
                        <td>
                          <ul>
                            <li>Evaluates the efficiency of testers.</li>
                            <li>
                              (Number of Test Cases Executed / Testing Hours
                              Spent)
                            </li>
                          </ul>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </section>

                {/* Test Artifacts */}
                <section className="content-block">
                  <h1>Test Artifacts</h1>
                  <p>
                    Test artifacts are essential documents created during
                    different phases of the Software Testing Life Cycle (STLC)
                    to maintain proper planning, execution, tracking, and
                    reporting of testing activities.
                  </p>
                  <table className="defect-matrics">
                    <thead>
                      <tr>
                        <th>Test Artifact</th>
                        <th>Description</th>
                        <th>Created During</th>
                      </tr>
                    </thead>

                    <tbody>
                      <tr>
                        <td>Test Plan</td>
                        <td>
                          A document outlining the test strategy, scope,
                          objectives, schedule, resources, and risks.
                        </td>
                        <td>Test Planning</td>
                      </tr>
                      <tr>
                        <td>Test Cases</td>
                        <td>
                          Detailed step-by-step test scripts specifying inputs,
                          expected results, and pass/fail criteria.
                        </td>
                        <td>Test Case Development</td>
                      </tr>
                      <tr>
                        <td>Test Scenarios</td>
                        <td>
                          High-level test descriptions covering multiple test
                          cases based on requirements.
                        </td>
                        <td>Test Case Development</td>
                      </tr>
                      <tr>
                        <td>Test Data</td>
                        <td>
                          Input data is used to execute test cases, including
                          valid, invalid, and boundary conditions.
                        </td>
                        <td>Test Case Development</td>
                      </tr>
                      <tr>
                        <td>Requirement Traceability Matrix (RTM)</td>
                        <td>
                          A mapping document linking test cases to their
                          corresponding requirements to ensure full coverage.
                        </td>
                        <td>Test Case Development</td>
                      </tr>
                      <tr>
                        <td>Test Environment Setup Document</td>
                        <td>
                          Specifies hardware, software, network configurations,
                          and access credentials required for testing.
                        </td>
                        <td>Test Environment Setup</td>
                      </tr>
                      <tr>
                        <td>Test Execution Report</td>
                        <td>
                          A daily/weekly summary of executed test cases,
                          pass/fail status, and any blockers.
                        </td>
                        <td>Test Execution</td>
                      </tr>
                      <tr>
                        <td>Defect Report</td>
                        <td>
                          A detailed log of identified defects, including steps
                          to reproduce, severity, priority, and status.
                        </td>
                        <td>Test Execution</td>
                      </tr>
                      <tr>
                        <td>Bug Report</td>
                        <td>
                          Captures information about a specific defect,
                          including actual vs. expected results and supporting
                          screenshots/logs.
                        </td>
                        <td>Test Execution</td>
                      </tr>
                      <tr>
                        <td>Test Summary Report</td>
                        <td>
                          A final document summarizes the testing process, test
                          execution results, defect status, and overall quality
                          assessment.
                        </td>
                        <td>Test Closure</td>
                      </tr>
                      <tr>
                        <td>Lessons Learned Document</td>
                        <td>
                          A post-testing review capturing challenges, best
                          practices, and recommendations for future projects.
                        </td>
                        <td>Test Closure</td>
                      </tr>
                    </tbody>
                  </table>
                </section>

                <h1>Conclusion</h1>
                <p>
                  Manual testing is an indispensable part of software quality
                  assurance. Mastering it requires understanding testing types,
                  writing effective test cases, and applying best practices.
                  Combining manual and automation testing ensures robust
                  software quality.
                </p>
                <hr />
                <div className="fusion-meta-info">
                  <div className="fusion-meta-info-wrapper">
                    By{" "}
                    <span className="vcard">
                      <span className="fn">
                        <a
                          href="#author/marketing_ek1lhmaa/"
                          title="Posts by Elly Johnson"
                          rel="author"
                        >
                          {" "}
                          Manish Prajapati{" "}
                        </a>
                      </span>
                    </span>
                    <span className="fusion-inline-sep">|</span>
                    <span className="updated rich-snippet-hidden">
                      {" "}
                      2025-07-23{" "}
                    </span>
                    <span> July 14th, 2025 </span>
                    <span className="fusion-inline-sep">|</span>
                    <a
                      href="#category/cheat-sheets/"
                      rel="category tag"
                    >
                      {" "}
                      Cheat Sheets{" "}
                    </a>
                    ,{" "}
                    <a
                      href="#category/manual-testing/"
                      rel="category tag"
                    >
                      {" "}
                      Manual Testing{" "}
                    </a>
                    <span className="fusion-inline-sep">|</span>
                    <span className="fusion-comments">
                      <span>
                        {" "}
                        Comments Off{" "}
                      </span>
                    </span>
                  </div>
                </div>
     
                <hr/>
              </div>
               {/* Sharing Box */}
                      <div className="sharing-box">
                        <h4>Share This Story, Choose Your Platform!</h4>
                        <div className="fusion-social-networks">
          
                  <NavLink to="https://www.linkedin.com/in/qa-coder-708418404/" target="_blank" rel="noreferrer" className="linkedin">
                    <i className="fab fa-linkedin-in"></i>
                  </NavLink>
                  <NavLink to="https://wa.me/918299775823" target="_blank" rel="noreferrer" className="whatsapp">
                    <i className="fab fa-whatsapp"></i>
                  </NavLink>
                  <NavLink to="mailto:qacoder0@gmail.com" className="email">
                    <i className="fas fa-envelope"></i>
                  </NavLink>
              {/* Instagram */}
              <NavLink to="https://www.instagram.com/qacoder0/?hl=en" target="_blank" rel="noreferrer" className="instagram">
                <i className="fab fa-instagram instagram-gradient"></i>
              </NavLink>
              
       
              
                </div>
                      </div>
            </article>
          </section>
        </div>
      </main>
    </div>
  );
}
